import { Link } from 'react-router'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'
import type { UserSchema, UserFormErrors } from '@klear/ui/lib/user-form-schema'
import type { UserRoleName } from '@klear/user/user-role'
import {
  ROLE_TRADER_ADMIN,
  ROLE_TRADER_FINANCIAL_ADMIN,
  ROLE_TRADER_USER,
  ROLE_TRADER_READONLY,
  ROLE_LABELS
} from '@klear/constants'

const TRADER_ROLES = [ROLE_TRADER_READONLY, ROLE_TRADER_USER, ROLE_TRADER_ADMIN, ROLE_TRADER_FINANCIAL_ADMIN] as const

type TraderTeamRole = (typeof TRADER_ROLES)[number]

const TRADER_ROLE_SUMMARIES: Record<TraderTeamRole, readonly string[]> = {
  [ROLE_TRADER_READONLY]: [
    'View customers, orders, invoices, transactions, financials, and reports',
    'Cannot create, edit, or delete records'
  ],
  [ROLE_TRADER_USER]: [
    'Run day-to-day work: customers, suppliers, orders, purchase orders, shipments, and invoices',
    'Cannot view or edit financials'
  ],
  [ROLE_TRADER_ADMIN]: [
    'Everything a user can do',
    'Configure organization, trader profile, and settings',
    'Manage customers, suppliers, orders, invoices, and most operational workflows',
    'Treasury and financing view only'
  ],
  [ROLE_TRADER_FINANCIAL_ADMIN]: [
    'Everything an operations manager can do for operations, plus full treasury and cash management',
    'Accept or reject financing offers',
    'Approve and send payments'
  ]
}

interface TeamUserFormProps {
  values: UserSchema
  errors: UserFormErrors
  onChange: (patch: Partial<UserSchema>) => void
  isNewUser: boolean
  onSubmit: () => void | Promise<void>
  cancelPath: string
  title: string
  submitting: boolean
}

export default function TeamUserForm({
  values,
  errors,
  onChange,
  isNewUser,
  onSubmit,
  cancelPath,
  title,
  submitting
}: TeamUserFormProps) {
  const currentRole = values.roles?.[0]?.role as UserRoleName | undefined

  const handleRoleChange = (role: TraderTeamRole) => {
    const traderId = values.roles?.[0]?.traderId
    onChange({ roles: [{ role, traderId }] })
  }

  return (
    <div className="p-6">
      <div className="bg-card rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            <Link to={cancelPath} className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70">
              Cancel
            </Link>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void onSubmit()
          }}
        >
          <div className="space-y-6 p-6">
            {errors.root && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 rounded-md">
                {errors.root}
              </div>
            )}

            <section>
              <h2 className="text-lg font-medium text-foreground mb-4">User Information</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email</Label>
                  <Input
                    id="email"
                    value={values.email}
                    disabled={!isNewUser}
                    onChange={(e) => onChange({ email: e.target.value })}
                    className={cn(!isNewUser ? 'bg-muted' : '', errors.email ? 'border-destructive' : '')}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name*</Label>
                  <Input
                    id="firstName"
                    value={values.firstName}
                    onChange={(e) => onChange({ firstName: e.target.value })}
                    className={errors.firstName ? 'border-destructive' : ''}
                  />
                  {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="middleName">Middle Name</Label>
                  <Input
                    id="middleName"
                    value={values.middleName ?? ''}
                    onChange={(e) => onChange({ middleName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name*</Label>
                  <Input
                    id="lastName"
                    value={values.lastName}
                    onChange={(e) => onChange({ lastName: e.target.value })}
                    className={errors.lastName ? 'border-destructive' : ''}
                  />
                  {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone</Label>
                  <Input
                    id="phoneNumber"
                    value={values.phoneNumber ?? ''}
                    onChange={(e) => onChange({ phoneNumber: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Job Title*</Label>
                  <Input
                    id="jobTitle"
                    value={values.jobTitle}
                    onChange={(e) => onChange({ jobTitle: e.target.value })}
                    className={errors.jobTitle ? 'border-destructive' : ''}
                  />
                  {errors.jobTitle && <p className="text-sm text-destructive">{errors.jobTitle}</p>}
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-medium text-foreground mb-1">Role</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Choose what this person can see and do. Only Organization Owners can invite people or change roles for
                others.
              </p>
              <div className={errors.roles ? 'border-l-4 border-red-500 pl-3' : ''}>
                <div className="space-y-3">
                  {TRADER_ROLES.map((role) => {
                    const selected = currentRole === role
                    return (
                      <div
                        key={role}
                        className={cn(
                          'flex gap-3 rounded-lg border p-3 transition-colors',
                          selected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-border'
                        )}
                      >
                        <input
                          id={`role-${role}`}
                          type="radio"
                          name="role"
                          className="mt-1 h-4 w-4 shrink-0 text-primary focus:ring-primary border-border"
                          checked={selected}
                          onChange={() => handleRoleChange(role)}
                        />
                        <div className="min-w-0 flex-1">
                          <label
                            htmlFor={`role-${role}`}
                            className="block cursor-pointer text-sm font-semibold text-foreground"
                          >
                            {ROLE_LABELS[role]}
                          </label>
                          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                            {TRADER_ROLE_SUMMARIES[role].map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )
                  })}
                  {errors.roles && <p className="mt-1 text-sm text-red-500">{errors.roles}</p>}
                </div>
              </div>
            </section>

            <div className="flex justify-end mt-6 gap-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <Link to={cancelPath} className="text-white bg-muted hover:bg-muted/70 py-2 px-4 rounded">
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
